import React from 'react'
import { useTranslation } from 'react-i18next'

import { BuildCircle, FiberNew, Palette as PaletteIcon, QuestionMark } from '@mui/icons-material'
import { Avatar } from '@mui/material'

import { GRTooltip } from '../../../../components/GRTooltip/GRTooltip'
import { theme } from '../../../../themes/theme'
import { LiveEventEvolutionTypeEnum } from '../../types/LiveEventCollection'
import { getLiveEventEvolutionTypeTranslation } from '../../utils/utils'
import './LiveEventEvolutionTypeChip.scss'

interface LiveEventEvolutionTypeChipProps {
  type: LiveEventEvolutionTypeEnum
  colorHex?: string
}

// Canonical per-type color, exported so other surfaces (e.g. the Market Analytics games
// drill-down, the Blueprint tab tags) render the same reskin/mechanic-change coloring outside
// this avatar shape. Mechanic change takes `info` rather than `primary`, which already marks
// "first seen" next to it.
export const LIVE_EVENT_EVOLUTION_TYPE_COLOR: Partial<Record<LiveEventEvolutionTypeEnum, string>> = {
  [LiveEventEvolutionTypeEnum.Reskin]: theme.palette.success.main,
  [LiveEventEvolutionTypeEnum.MechanicChange]: theme.palette.info.main,
}

export const LiveEventEvolutionTypeChip: React.FC<LiveEventEvolutionTypeChipProps> = ({ type, colorHex }) => {
  const { t } = useTranslation()
  const typeColor = LIVE_EVENT_EVOLUTION_TYPE_COLOR[type]

  const getIcon = () => {
    switch (type) {
      case LiveEventEvolutionTypeEnum.Highlighted:
        return <FiberNew />
      case LiveEventEvolutionTypeEnum.Reskin:
        return <PaletteIcon />
      case LiveEventEvolutionTypeEnum.MechanicChange:
        return <BuildCircle />
      default:
        return <QuestionMark />
    }
  }

  return (
    <GRTooltip
      content={
        type === LiveEventEvolutionTypeEnum.Highlighted
          ? t('live-events:live_event_evolution_type_first_appearance')
          : t(getLiveEventEvolutionTypeTranslation(type) || '')
      }
    >
      {type === LiveEventEvolutionTypeEnum.Highlighted ? (
        <div className="LiveEventEvolutionTypeChip LiveEventEvolutionTypeChip--highlighted" style={{ backgroundColor: colorHex, borderColor: colorHex }}>
          {getIcon()}
        </div>
      ) : (
        <Avatar
          className="LiveEventEvolutionTypeChip"
          data-type={type}
          sx={typeColor ? { bgcolor: typeColor, borderColor: typeColor, color: 'common.white' } : undefined}
        >
          {getIcon()}
        </Avatar>
      )}
    </GRTooltip>
  )
}
